import { memo } from 'react';
import { Button } from '@components/ui/button';
import type { HomeEmptyStateProps } from '../types';

/**
 * Empty state component for the home feed
 *
 * Best practices applied:
 * - Wrapped with memo() to prevent unnecessary re-renders
 * - Config-driven variants (cleaner than switch/if chains)
 * - Named function for better React DevTools debugging
 */
export const HomeEmptyState = memo(function HomeEmptyState({
  sortBy,
  onCreateQuestion,
}: HomeEmptyStateProps) {
  // Bookmarked empty state
  if (sortBy === 'bookmarked') {
    return (
      <EmptyStateCard>
        <h3 className="text-xl font-bold text-gray-900 mb-3">No bookmarked posts yet</h3>
        <p className="text-gray-600 mb-3 leading-relaxed font-medium">
          Bookmark posts to save them for later
        </p>
        <p className="text-gray-500 text-sm mb-6">Posts you bookmark will appear here.</p>
      </EmptyStateCard>
    );
  }

  // Meetverse empty state
  return (
    <EmptyStateCard>
      <h3 className="text-xl font-bold text-gray-900 mb-3">Post what you're looking for</h3>
      <p className="text-gray-600 mb-6 leading-relaxed font-medium">We'll show you who can help.</p>
      <Button onClick={onCreateQuestion} className="bg-mb-teal hover:bg-mb-teal-dark text-mb-ink">
        Share what you're looking for
      </Button>
    </EmptyStateCard>
  );
});

/**
 * Wrapper card for empty state content
 */
function EmptyStateCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-xs p-12 max-w-md text-center">
        <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
          <img src="/home-bottom-nav-icon.svg" alt="" className="w-16 h-16" aria-hidden="true" />
        </div>
        {children}
      </div>
    </div>
  );
}
